import React from 'react';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faSpinner, faTrash} from '@fortawesome/free-solid-svg-icons';

import type {IShortTermHistorySubStepProps} from '../productionInfo.types';

const faPlusIcon = faPlus as unknown as IconProp;
const faSpinnerIcon = faSpinner as unknown as IconProp;
const faTrashIcon = faTrash as unknown as IconProp;

export const ShortTermHistorySubStep: React.FC<
  IShortTermHistorySubStepProps
> = ({
  requiredShortTermHistoryYears,
  selectedShortTermHistoryCropToAdd,
  addableShortTermHistoryCropOptions,
  shortTermHistoryGroupedRows,
  shortTermHistoryTableErrors,
  shortTermHistoryTableErrorMessage,
  productionHistoryMonthOptions,
  productionHistoryYearOptions,
  isLoadingShortTermHistory,
  isSavingShortTermHistory,
  hasAttemptedShortTermHistoryLoad,
  onSelectedShortTermHistoryCropToAddChange,
  onAddShortTermHistoryCropGroup,
  onUpdateShortTermHistoryRowField,
  onRemoveShortTermHistoryCropGroup,
}) => (
  <div className="application-card production-history">
    <div className="production-history__toolbar">
      <div>
        <h3>Historic production records</h3>
        <p>
          Add a crop to create a fixed 3-year block (
          {requiredShortTermHistoryYears.join(', ')}). Crop and season are
          locked to prevent duplicates.
        </p>
      </div>

      <div className="production-history__toolbar-actions">
        <label className="sr-only" htmlFor="history-add-crop">
          Crop to add
        </label>
        <select
          id="history-add-crop"
          className="production-history__add-crop-select"
          value={selectedShortTermHistoryCropToAdd}
          onChange={(event) =>
            onSelectedShortTermHistoryCropToAddChange(event.target.value)
          }
          disabled={
            isLoadingShortTermHistory ||
            isSavingShortTermHistory ||
            addableShortTermHistoryCropOptions.length === 0
          }
        >
          <option value="">Select crop</option>
          {addableShortTermHistoryCropOptions.map((crop) => (
            <option key={crop.id} value={crop.id}>
              {crop.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="money-btn money-btn--secondary money-btn--sm"
          onClick={onAddShortTermHistoryCropGroup}
          disabled={
            isLoadingShortTermHistory ||
            isSavingShortTermHistory ||
            addableShortTermHistoryCropOptions.length === 0 ||
            !selectedShortTermHistoryCropToAdd
          }
        >
          <FontAwesomeIcon icon={faPlusIcon} />
          <span>Add crop</span>
        </button>
      </div>
    </div>

    <div className="production-history__body" aria-live="polite">
      {isLoadingShortTermHistory ? (
        <div className="production-history__loading" role="status">
          <FontAwesomeIcon icon={faSpinnerIcon} spin />
          <p>Loading your production history…</p>
        </div>
      ) : (
        <>
          {shortTermHistoryTableErrorMessage ? (
            <div className="form-alert is-error" role="alert">
              {shortTermHistoryTableErrorMessage}
            </div>
          ) : null}

          {shortTermHistoryGroupedRows.length > 0 ? (
            <div className="production-history__table-wrap">
              <table className="production-history-table">
                <thead>
                  <tr>
                    <th scope="col">Crop</th>
                    <th scope="col">Season</th>
                    <th scope="col">Hectares farmed</th>
                    <th scope="col">Yield/ha</th>
                    <th scope="col">Plant month</th>
                    <th scope="col">Plant year</th>
                    <th scope="col">Harvest month</th>
                    <th scope="col">Harvest year</th>
                    <th scope="col">Notes</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {shortTermHistoryGroupedRows.flatMap((group) =>
                    group.rows.map((row, rowIndex) => {
                      const rowErrors =
                        shortTermHistoryTableErrors[row.rowId] || {};
                      const hasRowErrors = Object.keys(rowErrors).length > 0;
                      const isGroupStart = rowIndex === 0;

                      return (
                        <tr
                          key={row.rowId}
                          className={`${hasRowErrors ? 'is-row-error' : ''} ${
                            isGroupStart ? 'is-group-start' : ''
                          }`.trim()}
                        >
                          {isGroupStart ? (
                            <td
                              rowSpan={group.rows.length}
                              className="production-history-table__crop-cell"
                            >
                              <span className="production-history-table__crop-name">
                                {group.cropName}
                              </span>
                              <span className="production-history-table__crop-subtitle">
                                {requiredShortTermHistoryYears.join(' / ')}{' '}
                                block
                              </span>
                            </td>
                          ) : null}

                          <td>
                            <div className="production-history-table__season">
                              <span className="production-history-table__season-pill">
                                {row.year}
                              </span>
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-hectares-${row.rowId}`}
                              >
                                Hectares farmed for {group.cropName} {row.year}
                              </label>
                              <input
                                id={`history-hectares-${row.rowId}`}
                                className="production-history-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.numberOfHectaresFarmed}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'numberOfHectaresFarmed',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              />
                              {rowErrors.numberOfHectaresFarmed ? (
                                <span className="production-history-table__error">
                                  {rowErrors.numberOfHectaresFarmed}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-yield-${row.rowId}`}
                              >
                                Yield per hectare for {group.cropName}{' '}
                                {row.year}
                              </label>
                              <input
                                id={`history-yield-${row.rowId}`}
                                className="production-history-table__control"
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={row.yieldPerHectare}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'yieldPerHectare',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              />
                              {rowErrors.yieldPerHectare ? (
                                <span className="production-history-table__error">
                                  {rowErrors.yieldPerHectare}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-plant-month-${row.rowId}`}
                              >
                                Plant month for {group.cropName} {row.year}
                              </label>
                              <select
                                id={`history-plant-month-${row.rowId}`}
                                className="production-history-table__control"
                                value={row.plantMonth}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'plantMonth',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              >
                                <option value="">Select month</option>
                                {productionHistoryMonthOptions.map(
                                  (monthOption) => (
                                    <option
                                      key={monthOption.value}
                                      value={monthOption.value}
                                    >
                                      {monthOption.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-plant-year-${row.rowId}`}
                              >
                                Plant year for {group.cropName} {row.year}
                              </label>
                              <select
                                id={`history-plant-year-${row.rowId}`}
                                className="production-history-table__control"
                                value={row.plantYear}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'plantYear',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              >
                                <option value="">Select year</option>
                                {productionHistoryYearOptions.map(
                                  (yearOption) => (
                                    <option
                                      key={yearOption.value}
                                      value={yearOption.value}
                                    >
                                      {yearOption.label}
                                    </option>
                                  ),
                                )}
                              </select>
                              {rowErrors.plantYear ? (
                                <span className="production-history-table__error">
                                  {rowErrors.plantYear}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-harvest-month-${row.rowId}`}
                              >
                                Harvest month for {group.cropName} {row.year}
                              </label>
                              <select
                                id={`history-harvest-month-${row.rowId}`}
                                className="production-history-table__control"
                                value={row.harvestMonth}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'harvestMonth',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              >
                                <option value="">Select month</option>
                                {productionHistoryMonthOptions.map(
                                  (monthOption) => (
                                    <option
                                      key={monthOption.value}
                                      value={monthOption.value}
                                    >
                                      {monthOption.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-harvest-year-${row.rowId}`}
                              >
                                Harvest year for {group.cropName} {row.year}
                              </label>
                              <select
                                id={`history-harvest-year-${row.rowId}`}
                                className="production-history-table__control"
                                value={row.harvestYear}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'harvestYear',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                              >
                                <option value="">Select year</option>
                                {productionHistoryYearOptions.map(
                                  (yearOption) => (
                                    <option
                                      key={yearOption.value}
                                      value={yearOption.value}
                                    >
                                      {yearOption.label}
                                    </option>
                                  ),
                                )}
                              </select>
                              {rowErrors.harvestYear ? (
                                <span className="production-history-table__error">
                                  {rowErrors.harvestYear}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td>
                            <div className="production-history-table__field">
                              <label
                                className="sr-only"
                                htmlFor={`history-notes-${row.rowId}`}
                              >
                                Notes for {group.cropName} {row.year}
                              </label>
                              <textarea
                                id={`history-notes-${row.rowId}`}
                                className="production-history-table__control production-history-table__control--notes"
                                rows={3}
                                value={row.notes}
                                onChange={(event) =>
                                  onUpdateShortTermHistoryRowField(
                                    row.rowId,
                                    'notes',
                                    event.target.value,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                                placeholder="Optional notes"
                              />
                              {rowErrors.general ? (
                                <span className="production-history-table__error">
                                  {rowErrors.general}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          {isGroupStart ? (
                            <td
                              rowSpan={group.rows.length}
                              className="production-history-table__actions"
                            >
                              <button
                                type="button"
                                className="money-btn money-btn--danger money-btn--sm"
                                onClick={() =>
                                  onRemoveShortTermHistoryCropGroup(
                                    group.cropId,
                                  )
                                }
                                disabled={isSavingShortTermHistory}
                                title={`Remove ${group.cropName}`}
                              >
                                <FontAwesomeIcon icon={faTrashIcon} />
                                <span className="sr-only">
                                  Remove {group.cropName}
                                </span>
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
          ) : hasAttemptedShortTermHistoryLoad ? (
            <div className="production-history__empty">
              <h3>No production history captured yet</h3>
              <p>
                Add a crop to generate the fixed{' '}
                {requiredShortTermHistoryYears.join(', ')} seasonal rows for
                editing.
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  </div>
);
